// The AI reviewer's visual identity, shared across the page. Same language
// as AI Discovery on the Discover page: a gold sparkle on a navy gradient
// tile, and a soft lavender-to-cream wash behind AI output.
import React from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AspectRatioOutlinedIcon from '@mui/icons-material/AspectRatioOutlined';
import BurstModeOutlinedIcon from '@mui/icons-material/BurstModeOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import MoodOutlinedIcon from '@mui/icons-material/MoodOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { C, Pill } from './ui';

export const AI = {
    tile: 'linear-gradient(135deg, #111936 0%, #2b3a78 100%)',
    wash: 'linear-gradient(135deg, #f4f1ff 0%, #eef5ff 50%, #fff7e6 100%)',
    washBorder: '#dcdff3',
    panel: 'linear-gradient(160deg, #111936 0%, #1b2550 60%, #232f63 100%)',
    lavender: '#ede9fe', ink: '#3730a3',
};

export function AiMark({ size = 36, active = false, style }) {
    return (
        <span aria-hidden="true" style={{
            width: size, height: size, borderRadius: Math.round(size * 0.3), background: AI.tile, flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 18px -10px rgba(17,25,54,0.65)', ...style,
        }}>
            <AutoAwesomeIcon className={active ? 'vqc-twinkle' : undefined} style={{ color: C.gold, fontSize: Math.round(size * 0.52) }} />
        </span>
    );
}

// Small "AI" label for anything the model produced.
export const AiTag = ({ children = 'AI' }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 999, background: AI.lavender, color: AI.ink, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
        <AutoAwesomeIcon aria-hidden="true" style={{ fontSize: 11, color: '#6d28d9' }} />
        {children}
    </span>
);

// A requirement that fails the video when missed.
export const CriticalTag = () => (
    <Pill bg="#fee2e2" color="#991b1b" title="Missing this fails the video"><FlagRoundedIcon aria-hidden="true" style={{ fontSize: 12 }} />Critical</Pill>
);

export const Skeleton = ({ width = '100%', height = 12, radius = 6, style }) => (
    <span aria-hidden="true" className="vqc-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

const DIMENSION_ICON = {
    spoken: RecordVoiceOverOutlinedIcon,
    visual: VisibilityOutlinedIcon,
    format: AspectRatioOutlinedIcon,
    tone: MoodOutlinedIcon,
};

export function DimensionIcon({ id, size = 15, color = C.muted }) {
    const Icon = DIMENSION_ICON[id] || VisibilityOutlinedIcon;
    return <Icon aria-hidden="true" style={{ fontSize: size, color }} />;
}

const STEPS = [
    { label: 'Samples frames', Icon: BurstModeOutlinedIcon },
    { label: 'Transcribes the audio', Icon: GraphicEqRoundedIcon },
    { label: 'Checks every requirement', Icon: FactCheckOutlinedIcon },
    { label: 'You make the call', Icon: PersonOutlineRoundedIcon },
];

// What the AI reviewer does with each video, in order. `dark` is for the navy hero.
export function HowItWorks({ dark = false, style }) {
    const chip = dark
        ? { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.88)' }
        : { background: '#fff', border: `1px solid ${C.border}`, color: C.body };
    return (
        <ol aria-label="How Video QC works" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, listStyle: 'none', padding: 0, margin: 0, ...style }}>
            {STEPS.map(({ label, Icon }, i) => (
                <li key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, ...chip }}>
                        <Icon aria-hidden="true" style={{ fontSize: 15, color: i === STEPS.length - 1 ? (dark ? '#fff' : C.navy) : (dark ? C.gold : '#c79309') }} />
                        {label}
                    </span>
                    {i < STEPS.length - 1 && <ChevronRightIcon aria-hidden="true" style={{ fontSize: 16, color: dark ? 'rgba(255,255,255,0.4)' : C.faint }} />}
                </li>
            ))}
        </ol>
    );
}
